import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Loader2, RefreshCw, Upload, XCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
    queued: "bg-slate-100 text-slate-700 border-slate-300",
    running: "bg-blue-100 text-blue-800 border-blue-300",
    completed: "bg-green-100 text-green-800 border-green-300",
    failed: "bg-red-100 text-red-700 border-red-300",
    cancelled: "bg-amber-100 text-amber-800 border-amber-300",
};

const ACTIONS = [
    { key: "psc-dry", label: "PSC sync (dry run)", path: "/sourcing/ingest/psc/sync", body: { dry_run: true }, hint: "Counts only — run this first on real data" },
    { key: "psc", label: "PSC sync", path: "/sourcing/ingest/psc/sync", body: { dry_run: false }, hint: "Owner ages + succession signal" },
    { key: "accounts-proto", label: "Accounts prototype check", path: "/sourcing/ingest/accounts/trigger", body: { mode: "prototype" }, hint: "Accuracy report on 15 real filings", inline: true },
    { key: "accounts", label: "Accounts sync", path: "/sourcing/ingest/accounts/trigger", body: { mode: "monthly_bulk" }, hint: "Needs CH_ACCOUNTS_ACTIVE=true" },
    { key: "pl-census", label: "P&L coverage census", path: "/sourcing/ingest/pl-census", body: { sample_every: 20 }, hint: "Read-only. How many filings carry a P&L (Engine B go/no-go)" },
    { key: "turnover", label: "Turnover estimates", path: "/sourcing/ingest/turnover/sync", body: null, hint: "Shared estimates for mandate matching" },
];

function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-GB"); } catch { return iso; }
}

function progressText(job) {
    const p = job.progress || {};
    if (p.members_total) return `${p.file_name || ""} ${p.members_done}/${p.members_total}`;
    if (p.raw_lines_done) {
        const n = (v) => Number(v || 0).toLocaleString();
        const extra = [p.n_skipped_non_psc != null && `${n(p.n_skipped_non_psc)} statements`, p.n_unknown_company != null && `${n(p.n_unknown_company)} not in company list`].filter(Boolean);
        return `${p.file_name || ""} ${n(p.raw_lines_done)} lines read, ${n(p.n_upserted)} added${extra.length ? `, ${extra.join(", ")}` : ""}`;
    }
    if (p.companies) return `${Number(p.companies).toLocaleString()} companies`;
    if (p.phase) return `${p.file_name || ""} ${p.phase}`;
    return "";
}

function resultText(job) {
    if (job.error) return job.error;
    if (!job.result) return "";
    if (job.result.summary) return job.result.summary;
    return JSON.stringify(job.result).slice(0, 90);
}

function resultTitle(job) {
    const r = job.result?.report;
    if (!r) return job.error || JSON.stringify(job.result || {});
    const rows = Object.entries(r.by_accounts_type || {}).slice(0, 8).map(([k, v]) => `${k}: ${v.with_pl}/${v.total} (${v.pct}%)`);
    return [job.result.summary, "", "By accounts type:", ...rows].join("\n");
}

function CsvUpload({ label, path, columns, onDone }) {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${label}: ${data.loaded} rows loaded`);
            onDone();
        } catch (err) {
            const d = err?.response?.data?.detail;
            const first = d?.errors?.[0];
            toast.error(first ? `${label}: line ${first.line} — ${first.error}` : formatApiErrorDetail(d) || "Upload failed");
        } finally {
            setBusy(false);
            if (ref.current) ref.current.value = "";
        }
    };
    return (
        <div className="space-y-1">
            <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" onChange={submit} data-testid={`upload-${label}`} />
            <Button variant="outline" size="sm" disabled={busy} onClick={() => ref.current?.click()}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                Upload {label} CSV
            </Button>
            <p className="text-[11px] text-[#94A3B8] max-w-md">Columns: {columns}. Replaces the existing table.</p>
        </div>
    );
}

export default function PipelineAdmin() {
    const [jobs, setJobs] = useState([]);
    const [execution, setExecution] = useState(null);
    const [ref, setRef] = useState(null);
    const [busyKey, setBusyKey] = useState(null);

    const load = async () => {
        try {
            const [j, r] = await Promise.all([api.get("/sourcing/ingest/jobs?limit=12"), api.get("/sourcing/admin/sector-reference")]);
            setJobs(j.data.jobs || []);
            setExecution(j.data.execution);
            setRef(r.data);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, []);

    const run = async (a) => {
        setBusyKey(a.key);
        try {
            const { data } = await api.post(a.path, a.body ?? undefined);
            toast.success(`${a.label}: ${data.status || "queued"}`);
            load();
        } catch (err) {
            toast.error(`${a.label}: ${formatApiErrorDetail(err?.response?.data?.detail) || err.message}`);
        } finally {
            setBusyKey(null);
        }
    };

    const cancel = async (id) => {
        try { await api.post(`/sourcing/ingest/jobs/${id}/cancel`); load(); } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Cancel failed");
        }
    };

    return (
        <Card data-testid="pipeline-admin">
            <CardHeader className="pb-3">
                <CardTitle className="text-[15px] flex items-center justify-between">
                    <span>Data pipeline (worker jobs)</span>
                    <span className="flex items-center gap-2 text-[12px] font-normal text-[#64748B]">
                        {execution && <Badge variant="outline">{execution === "worker" ? "worker mode" : "inline mode — worker jobs disabled"}</Badge>}
                        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="w-3.5 h-3.5" /></Button>
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
                <div className="flex flex-wrap gap-2">
                    {ACTIONS.map(a => (
                        <div key={a.key} className="flex flex-col">
                            <Button variant={a.key === "psc-dry" ? "default" : "outline"} size="sm" disabled={busyKey === a.key || (!a.inline && execution !== "worker")}
                                onClick={() => run(a)} data-testid={`action-${a.key}`}>
                                {busyKey === a.key && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}{a.label}
                            </Button>
                            <span className="text-[10px] text-[#94A3B8] mt-0.5">{a.hint}</span>
                        </div>
                    ))}
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead className="text-[11px] text-[#94A3B8] uppercase tracking-wider">
                            <tr><th className="text-left py-1.5 pr-3">Job</th><th className="text-left pr-3">Status</th><th className="text-left pr-3">Progress</th><th className="text-left pr-3">Created</th><th className="text-left">Result / error</th><th /></tr>
                        </thead>
                        <tbody>
                            {jobs.length === 0 && <tr><td colSpan={6} className="py-3 text-[#94A3B8]">No jobs yet.</td></tr>}
                            {jobs.map(j => (
                                <tr key={j.id} className="border-t border-[#F1F5F9]" data-testid={`job-${j.id}`}>
                                    <td className="py-1.5 pr-3 font-mono">{j.type}</td>
                                    <td className="pr-3"><Badge variant="outline" className={STATUS_STYLE[j.status] || ""}>{j.status}</Badge></td>
                                    <td className="pr-3 text-[#475569]">{progressText(j)}</td>
                                    <td className="pr-3 text-[#64748B] whitespace-nowrap">{fmt(j.created_at)}</td>
                                    <td className={`text-[#64748B] max-w-[320px] ${j.result?.summary ? "" : "truncate"}`} title={resultTitle(j)}>
                                        {resultText(j)}
                                    </td>
                                    <td>{(j.status === "queued" || j.status === "running") && (
                                        <Button variant="ghost" size="sm" onClick={() => cancel(j.id)} title="Cancel"><XCircle className="w-3.5 h-3.5 text-red-500" /></Button>)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="border-t border-[#E2E8F0] pt-4 space-y-3">
                    <p className="text-[12px] text-[#475569]">
                        Sector reference data{ref ? ` — ${ref.benchmarks} benchmark rows, ${ref.multiples} multiples rows loaded` : ""}.
                        Until loaded, valuations use clearly flagged placeholder values and are capped at Low confidence.
                    </p>
                    <div className="flex flex-wrap gap-6">
                        <CsvUpload label="benchmarks" path="/sourcing/admin/sector-benchmarks/upload" onDone={load}
                            columns="sic_prefix, net_margin, ebitda_margin, credit_days, revenue_per_employee, asset_turnover, cash_led, source, as_of" />
                        <CsvUpload label="multiples" path="/sourcing/admin/sector-multiples/upload" onDone={load}
                            columns="sic_prefix, ev_ebitda_low, ev_ebitda_mid, ev_ebitda_high, source, as_of" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
